/**
 * applyCssVariables.ts — Injects the token CSS variables onto the document at startup.
 *
 * Called synchronously from `index.tsx` before React renders, so `--color-*` vars exist
 * on `:root` before first paint (no FOUC). The `.dark` overrides are emitted into a single
 * injected <style> tag so they apply whenever the `dark` class is toggled on <html>.
 */
import { buildRootVars, buildDarkVars } from './cssVariables';

const STYLE_ELEMENT_ID = 'streamliner-design-tokens';

function serializeVars(vars: Record<string, string>): string {
  return Object.entries(vars)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n  ');
}

export function applyCssVariables(doc: Document = document): void {
  const root = doc.documentElement;

  // Set :root vars directly so they're available before any stylesheet/paint.
  const rootVars = buildRootVars();
  for (const [key, value] of Object.entries(rootVars)) {
    root.style.setProperty(key, value);
  }

  // Dark-mode overrides need a `.dark` selector, so they go in a <style> tag.
  if (!doc.getElementById(STYLE_ELEMENT_ID)) {
    const style = doc.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = `.dark {\n  ${serializeVars(buildDarkVars())}\n}`;
    doc.head.appendChild(style);
  }
}

export default applyCssVariables;
